import type { HorizonItem } from "@beresta/core";
import { CheckBox, Icon, Mark } from "@beresta/ui";
import { useState } from "react";
import { type MessageKey, t } from "../i18n.js";

/**
 * Рядки плану (картка PlanItem): квадрат, текст і, якщо є що сказати,
 * позначка. Зроблене — лайм у квадраті й `ink-3`, **без закреслення**:
 * закреслення читається як «викреслив і забув», а пункт лишається
 * частиною дня.
 *
 * Перенесене несе слід «перенесено ×2» на `mark-note` — спостереження, а не
 * докір. У тісному блоці тижня напис коротшає до «×2»: там уже є контекст.
 *
 * Ціль дотику — увесь рядок заввишки `tap-min`, а не квадрат 22.
 */
export interface PlanListProps {
  readonly items: readonly HorizonItem[];
  readonly onToggle: (id: string) => void;
  /** Щільний блок тижня: коротші сліди, дрібніший текст, «ще N». */
  readonly dense?: boolean;
  /** Скільки рядків показати. Решта згортається в «ще N» (I9). */
  readonly limit?: number;
  readonly rollCounts?: ReadonlyMap<string, number>;
  /** Рядок «Додати пункт». Немає обробника — немає рядка. */
  readonly onAdd?: (text: string) => void;
  readonly addLabel?: MessageKey;
  /** Довгий натиск на пункті — B2 «Властивості пункту». */
  readonly onOpen?: (item: HorizonItem) => void;
  /** Поле «Додати пункт» уже відкрите — коли людина прийшла саме додати. */
  readonly adding?: boolean;
}

export function PlanList({
  items,
  onToggle,
  dense = false,
  limit,
  rollCounts,
  onAdd,
  addLabel = "plan.add",
  onOpen,
  adding = false,
}: PlanListProps) {
  const shown = limit === undefined ? items : items.slice(0, limit);
  const hidden = items.length - shown.length;

  return (
    <div className="block plan" data-dense={dense || undefined}>
      {shown.map((item) => (
        <PlanRow
          key={item.id}
          item={item}
          onToggle={onToggle}
          dense={dense}
          rolled={rollCounts?.get(item.id) ?? 0}
          {...(onOpen ? { onOpen } : {})}
        />
      ))}
      {hidden > 0 && (
        <p className="pi footnote t-ink-3" style={{ margin: 0 }}>
          {t("plan.more", { n: hidden })}
        </p>
      )}
      {onAdd && <AddRow label={addLabel} onAdd={onAdd} startOpen={adding} />}
    </div>
  );
}

function PlanRow({
  item,
  onToggle,
  dense,
  rolled,
  onOpen,
}: {
  readonly item: HorizonItem;
  readonly onToggle: (id: string) => void;
  readonly dense: boolean;
  readonly rolled: number;
  readonly onOpen?: (item: HorizonItem) => void;
}) {
  const done = item.doneAt !== null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const hold = onOpen
    ? {
        onPointerDown: () => {
          timer = setTimeout(() => onOpen(item), 500);
        },
        onPointerUp: () => clearTimeout(timer),
        onPointerLeave: () => clearTimeout(timer),
        onPointerCancel: () => clearTimeout(timer),
        onContextMenu: (e: { preventDefault: () => void }) => {
          e.preventDefault();
          // Шторка вже відкривається — таймер довгого натиску мусить згаснути
          // тут: `pointerup` упаде вже на шторку, і без цього таймер відкрив
          // би її вдруге, щойно людина встигла її закрити.
          clearTimeout(timer);
          onOpen(item);
        },
      }
    : {};
  return (
    <div className={`pi${done ? " pi-done" : ""}${item.spanEndKey ? " pi-span" : ""}`} data-item={item.id} {...hold}>
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={`${item.text} — ${done ? t("plan.markUndone") : t("plan.markDone")}`}
        className="pi-check"
        onClick={() => onToggle(item.id)}
      >
        <CheckBox on={done} />
      </button>
      <span className={`ptxt ${dense ? "subhead" : "body-ui"}`}>
        {/* Час — жирно перед текстом, як у картці ScreenDayGoogle. */}
        {item.startTime && <span className="ptime num">{item.startTime}</span>}
        {item.text}
      </span>
      {item.rolledFromId !== null && (
        <Mark tone="note" text="caption-1">
          {dense
            ? t("plan.rolledShort", { n: Math.max(rolled, 1) })
            : rolled > 0
              ? t("plan.rolledTimes", { n: rolled })
              : t("plan.rolled")}
        </Mark>
      )}
    </div>
  );
}

/**
 * «+ Додати пункт» — останній рядок блоку. Дотик перетворює його на поле в
 * тому самому місці: окремої форми під блоком немає, рядок і є запрошення.
 */
function AddRow({
  label,
  onAdd,
  startOpen,
}: {
  readonly label: MessageKey;
  readonly onAdd: (text: string) => void;
  readonly startOpen: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const [draft, setDraft] = useState("");
  if (!open)
    return (
      <button type="button" className="add body-ui" onClick={() => setOpen(true)}>
        <Icon name="plus" />
        {t(label)}
      </button>
    );
  return (
    <form
      className="add add-form"
      onSubmit={(e) => {
        e.preventDefault();
        const text = draft.trim();
        if (text === "") return;
        onAdd(text);
        setDraft("");
      }}
    >
      <Icon name="plus" />
      <input
        autoFocus
        className="body-ui"
        aria-label={t(label)}
        placeholder={t("day.addPlaceholder")}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft.trim() === "") setOpen(false);
        }}
      />
    </form>
  );
}
